import axios from 'axios';
import JSONbig from 'json-bigint';
import { store } from '../store';
import { clearAuth } from '../store/authSlice';
import notificationService, { formatBackendValidationWarning } from '../services/notificationService';
import authService from '../services/authService';
import SecureTokenUtils from './secureTokenUtils';
import { rateLimiter } from './rateLimiter';
import { classifyRateLimitEndpointType } from './rateLimitEndpointType';
import secureStorage from './secureStorage';
import { shouldRedirectAfterSessionFailure } from './authRoutePolicy';

let isSessionFailureInProgress = false;

const handleInvalidSession = () => {
  if (isSessionFailureInProgress) return;

  isSessionFailureInProgress = true;
  const shouldRedirect = shouldRedirectAfterSessionFailure(window.location.pathname);

  // The server has already rejected this session, so remove it locally without
  // making another authenticated logout request through the same interceptor.
  secureStorage.clearAll();
  store.dispatch(clearAuth());

  if (!shouldRedirect) {
    // Public pages remain usable in anonymous mode.
    isSessionFailureInProgress = false;
    return;
  }

  notificationService.sessionExpired();
  setTimeout(() => {
    window.location.href = '/login?expired=true';
  }, 1000);
};

/** User-service auth routes that must work without an existing session (login page flows). */
const PUBLIC_AUTH_URL_MARKERS = [
  '/auth/email/code/send',
  '/auth/email/code/verify',
  '/auth/email/forget/password',
  '/auth/email/register',
  '/auth/email/login',
  '/google/verify',
  '/google/login',
  '/google/health',
] as const;

const PUBLIC_NO_AUTH_URL_MARKERS = [
  '/tenant/leads',
] as const;

const isPublicAuthEndpoint = (url?: string | null): boolean => {
  if (!url) return false;

  const urlPath = url.split('?')[0];

  if (PUBLIC_AUTH_URL_MARKERS.some((marker) => urlPath.includes(marker))) {
    return true;
  }

  const legacyPublicPatterns = [
    /^.*\/captchaImage$/,
    /^.*\/register$/,
    /^.*\/login$/,
    /^.*\/api\/user\/auth\/register$/,
    /^.*\/api\/user\/auth\/login$/,
  ];

  return legacyPublicPatterns.some((pattern) => pattern.test(urlPath));
};

const isPublicNoAuthEndpoint = (url?: string | null): boolean => {
  if (!url) return false;
  const urlPath = url.split('?')[0];

  return (
    isPublicAuthEndpoint(url) ||
    PUBLIC_NO_AUTH_URL_MARKERS.some((marker) => urlPath.includes(marker))
  );
};

/** Auth UI / auth-service endpoints that handle errors locally (includes session-required routes). */
const isLocalAuthUiEndpoint = (url?: string | null): boolean => {
  if (!url) return false;

  return (
    isPublicAuthEndpoint(url) ||
    url.includes('/api/user/auth/') ||
    url.includes('/auth/email/thirdParty/complete/info') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/refresh/token')
  );
};

const isAuthorizationSuppressed = (headers: any): boolean => {
  if (!headers) return false;
  const read = (name: string) =>
    typeof headers.get === 'function' ? headers.get(name) : headers?.[name] ?? headers?.[name.toLowerCase()];
  const value = read('Authorization') ?? read('authorization');
  return value === false || value === 'false';
};

/** Axios v1: use `false` and delete so merged defaults do not re-attach Bearer. */
const clearAuthorizationHeader = (headers: any) => {
  if (!headers) return;

  if (typeof headers.set === 'function') {
    headers.delete('Authorization');
    headers.delete('authorization');
    headers.set('Authorization', false);
    headers.set('authorization', false);
    return;
  }

  delete headers.Authorization;
  delete headers.authorization;
  headers.Authorization = false;
  headers.authorization = false;
};

const shouldSuppressGlobalNotifications = (config: any): boolean => {
  if (!config) return false;
  const headers: any = config.headers ?? {};
  const readHeader = (name: string) => {
    // Axios v1 may use AxiosHeaders (with .get); older versions use plain objects.
    if (headers && typeof headers.get === 'function') return headers.get(name);
    return headers?.[name] ?? headers?.[name.toLowerCase()];
  };
  const headerValue = readHeader('X-Suppress-Global-Notification');

  return (
    Boolean((config as any).suppressGlobalErrorNotification) ||
    headerValue === true ||
    headerValue === 'true' ||
    headerValue === 1 ||
    headerValue === '1'
  );
};

const setupAxiosInterceptors = () => {
  const ENABLE_TOKEN_PREFLIGHT = process.env.REACT_APP_TOKEN_PREFLIGHT === 'true';
  // Set default axios timeout to 30 seconds
  axios.defaults.timeout = 30000;

  // Configure axios defaults for better reliability
  axios.defaults.headers.common['Cache-Control'] = 'no-cache';
  axios.defaults.headers.common['Pragma'] = 'no-cache';

  // Configure json-bigint to handle large numbers (e.g., agent/merchant IDs)
  // IMPORTANT: This will convert ALL large numbers to strings, not just agent IDs
  axios.defaults.transformResponse = [
    (data) => {
      if (typeof data === 'string') {
        try {
          // Parse JSON with big number support - stores large numbers as strings
          return JSONbig({ storeAsString: true }).parse(data);
        } catch (e) {
          // If parsing fails, return the raw data
          return data;
        }
      }
      return data;
    }
  ];

  // Request interceptor to add JWT token, check expiration, and rate limiting
  axios.interceptors.request.use(
    async (config) => {
      const suppressGlobalNotifications = shouldSuppressGlobalNotifications(config);

      const endpointType = classifyRateLimitEndpointType(config.url);

      // Check rate limiting
      const rateLimitResult = rateLimiter.isAllowed(endpointType);
      if (!rateLimitResult.allowed) {
        const error = new Error('Rate limit exceeded');
        (error as any).rateLimited = true;
        (error as any).retryAfter = rateLimitResult.retryAfter;
        if (!suppressGlobalNotifications) {
          notificationService.error(
            'Rate Limit Exceeded', 
            `Too many requests. Please try again in ${rateLimitResult.retryAfter} seconds.`
          );
        }
        return Promise.reject(error);
      }

      const token = secureStorage.getToken();
      const isPublicEndpoint = isPublicNoAuthEndpoint(config.url);

      // Optional: Check if token is expired before making the request (disabled by default)
      const isExpired = ENABLE_TOKEN_PREFLIGHT && token && !isPublicEndpoint
        ? await SecureTokenUtils.isTokenExpired(token)
        : false;
      if (isExpired) {
        handleInvalidSession();

        // Cancel the request
        return Promise.reject(new Error('Token expired'));
      }
      
      // Public auth flows (forgot password, email code, register, login) must not send
      // a stale Bearer token — the gateway may treat the call as session-required.
      if (isPublicEndpoint || isAuthorizationSuppressed(config.headers)) {
        clearAuthorizationHeader(config.headers);
      } else if (token) {
        if (typeof config.headers?.set === 'function') {
          config.headers.set('Authorization', `Bearer ${token}`);
        } else {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle JWT expiration and network errors
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const suppressGlobalNotifications = shouldSuppressGlobalNotifications(error.config);

      // Handle timeout errors
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        console.error('Request timeout:', error);
        if (!suppressGlobalNotifications) {
          notificationService.networkError();
        }
        return Promise.reject({
          ...error,
          message: 'Request timed out. Please try again.',
          isTimeout: true
        });
      }

      // Handle network errors (no response from server)
      if (!error.response) {
        console.error('Network error details:', {
          message: error.message,
          code: error.code,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL
          }
        });
        
        // Provide more specific error messages
        let errorMessage = 'Unable to connect to the server';
        if (error.code === 'ECONNREFUSED') {
          errorMessage = 'Unable to connect. Please try again later or contact support.';
        } else if (error.code === 'ENOTFOUND') {
          errorMessage = 'Server not found. Please check the API configuration';
        } else if (error.code === 'ERR_NETWORK') {
          errorMessage = 'Network error. Please check your internet connection';
        }
        
        // Show custom notification with specific error
        if (!suppressGlobalNotifications) {
          notificationService.error('Network Error', errorMessage);
        }
        return Promise.reject(error);
      }
      
      // Handle different HTTP error status codes
      const status = error.response?.status;
      const errorData = error.response?.data;
      
      const isAuthEndpoint = isLocalAuthUiEndpoint(error.config?.url);
      
      switch (status) {
        case 400:
          // Bad Request - Client sent invalid data
          console.error('Bad Request:', errorData);
          // Don't show notification for auth endpoints - they handle errors locally
          if (!isAuthEndpoint && !suppressGlobalNotifications) {
            const formatted = formatBackendValidationWarning(errorData);
            if (formatted) {
              // Show as warning (instructional) instead of generic error.
              notificationService.warning(formatted.title, formatted.description);
            } else if (errorData?.message) {
              notificationService.error('Invalid Request', errorData.message);
            }
          }
          break;
          
        case 403:
          // Forbidden - User doesn't have permission
          console.error('Forbidden:', errorData);
          if (!suppressGlobalNotifications) {
            notificationService.error('Access Denied', errorData?.message || 'You do not have permission to perform this action');
          }
          break;
          
        case 404:
          // Not Found - Resource doesn't exist
          console.error('Not Found:', errorData);
          // Don't show notification for 404s in most cases as they're handled by components
          break;
          
        case 409:
          // Conflict - Resource conflict (e.g., duplicate entry)
          console.error('Conflict:', errorData);
          // Don't show notification for auth endpoints - they handle errors locally
          if (!isAuthEndpoint && !suppressGlobalNotifications) {
            notificationService.error('Conflict', errorData?.message || 'A conflict occurred with your request');
          }
          break;
          
        case 429:
          // Too Many Requests - Rate limiting
          console.error('Too Many Requests:', errorData);
          if (!suppressGlobalNotifications) {
            notificationService.error('Too Many Requests', 'Please slow down and try again later');
          }
          break;
          
        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          console.error('Server Error:', errorData);
          if (!isAuthEndpoint && !suppressGlobalNotifications) {
            notificationService.error('Server Error', 'The server encountered an error. Please try again later');
          }
          break;
      }
      
      if (error.response?.status === 401) {
        // Auth flows (login/register/verify pages) handle auth errors locally.
        // Also: don't attempt refresh-token flow for auth endpoints.
        if (isAuthEndpoint || suppressGlobalNotifications) {
          return Promise.reject(error);
        }

        // Get the original request
        const originalRequest = error.config;
        
        // Prevent infinite loops - check if this is the refresh endpoint itself
        if (originalRequest.url?.includes('/auth/refresh/token')) {
          console.log('🔴 [REFRESH] Refresh token request failed - token expired');
          handleInvalidSession();
          return Promise.reject(error);
        }
        
        // Check if we already tried to refresh for this request (prevent infinite loop)
        if (originalRequest._retry) {
          console.log('🔴 [REFRESH] Already tried to refresh for this request - logging out');
          handleInvalidSession();
          return Promise.reject(error);
        }
        
        // Mark this request as retried
        originalRequest._retry = true;
        
        try {
          console.log('🔄 [REFRESH] Access token expired - attempting refresh...');
          
          // Try to refresh the token
          const authData = await authService.refreshAccessToken();
          
          console.log('✅ [REFRESH] Token refreshed successfully - retrying original request');
          
          // Update the failed request with new token
          originalRequest.headers.Authorization = `Bearer ${authData.token}`;
          
          // Retry the original request with new token
          return axios(originalRequest);
          
        } catch (refreshError) {
          // Refresh failed - logout user
          console.log('🔴 [REFRESH] Token refresh failed - logging out user');
          handleInvalidSession();
          return Promise.reject(refreshError);
        }
      }
      
      return Promise.reject(error);
    }
  );
};

export default setupAxiosInterceptors;
